import axios from 'axios';
import {
  removeToken,
  retrieveRefreshToken,
  retrieveToken,
  saveAccessToken,
  saveRefreshToken,
} from '../../utils/token';
import { HTTP_STATUS } from '@wareclouds/frontend-common';
import env from '@/config/env';

const API_AUTH_URL = env.AUTH_API_URL;

class RequestError extends Error {
  constructor(message, data, originalError) {
    super(message);
    this.name = 'RequestError';
    this.data = data;
    this.originalError = originalError;
  }
}

let refreshRequest = null;
let isLoggingOut = false;

function logoutAndReload() {
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;
  removeToken();
  window.location.reload();
}

function getRefreshedAccessToken(response) {
  return response?.data?.data?.accessToken ?? response?.data?.accessToken;
}

function getRefreshedRefreshToken(response) {
  return response?.data?.data?.refreshToken ?? response?.data?.refreshToken;
}

function refreshSession() {
  if (!refreshRequest) {
    refreshRequest = (async () => {
      const refreshToken = retrieveRefreshToken();

      if (!refreshToken) {
        throw new Error('NO_REFRESH_TOKEN');
      }

      const response = await axios.post(`${API_AUTH_URL}/token/refresh`, {
        token: refreshToken,
      });
      const accessToken = getRefreshedAccessToken(response);
      const nextRefreshToken = getRefreshedRefreshToken(response);

      if (!accessToken) {
        throw new Error('INVALID_REFRESH_RESPONSE');
      }

      saveAccessToken(accessToken);
      if (nextRefreshToken) {
        saveRefreshToken(nextRefreshToken);
      }

      return accessToken;
    })().finally(() => {
      refreshRequest = null;
    });
  }

  return refreshRequest;
}

export default function axiosInstance(baseURL) {
  const instance = axios.create({
    baseURL,
    timeout: 60000,
    maxRedirects: 10,
    maxContentLength: 50 * 1000 * 1000,
  });

  instance.interceptors.request.use((config) => {
    const accessToken = retrieveToken();

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      return response.data;
    },
    async (error) => {
      const { UNAUTHORIZED } = HTTP_STATUS;
      const originalRequest = error.config;
      const status = error.response?.status;

      if (
        status === UNAUTHORIZED &&
        originalRequest &&
        !originalRequest._retry &&
        !originalRequest._ignoreReload
      ) {
        originalRequest._retry = true;
        const refreshToken = retrieveRefreshToken();

        if (refreshToken) {
          try {
            const accessToken = await refreshSession();
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return instance(originalRequest);
          } catch (refreshError) {
            logoutAndReload();
            throw new RequestError(
              refreshError.message || refreshError.name,
              refreshError.response,
              refreshError
            );
          }
        }

        logoutAndReload();
      }

      throw new RequestError(
        error.message || error.name,
        error.response,
        error
      );
    }
  );

  return instance;
}
